import dayjs from "dayjs";

export type TIssue = {
  id?: number;
  coverImageUri?: string;
  readerUrl?: string;
  readerUrlWithPubs?: string;
};

export type TPreviewIssue = {
  id: number;
  publishedDate: string;
  coverImageUri: string;
  readerUrl: string;
};

type TIssueResponse = { issue?: TIssue };

export type ArchiveResponse = {
  issues?: TArchive[];
};

export type TArchive = {
  id?: number;
  coverImageUri?: string;
  publishedDate?: string;
  readerUrl?: string | null;
};

export const fetchArchives = async (pubsId: string) => {
  const resp = await fetch(`/_plat/api/v1/${pubsId}/archives`);

  const data = (await resp.json()) as ArchiveResponse;
  return data;
};

export const getIssueById = async ({
  issueId,
  pubId,
}: {
  issueId: number;
  pubId: string;
}) => {
  const resp = await fetch(`/_plat/api/v1/${pubId}/issue/${issueId}`);
  const jsonResp = (await resp.json()) as TIssueResponse;
  return jsonResp.issue;
};

/**
 * Fetches the current preview issues for a preview publication id
 * (e.g. `tmpreview`). Backed by the public BFF preview API (EPPR2-3098); the
 * returned web readerUrl already has the preview pubId injected into its path,
 * so it resolves to the un-gated preview reader.
 */
export const fetchPreviewArchive = async (
  previewPubId: string,
): Promise<{ issues: TPreviewIssue[] }> => {
  const resp = await fetch(
    `/_plat/api/v1/preview/archive/${previewPubId}?platform=web`,
  );
  const data = (await resp.json()) as {
    data?: { issues?: TPreviewIssue[] };
  };
  return { issues: data.data?.issues ?? [] };
};

/**
 * Picks the preview issue matching the displayed issue's date (PRD: current
 * day only), falling back to the latest preview issue. Uses the same dayjs
 * day-grouping the Home archive list uses, so "today" lines up across both.
 * Returns null only when there are no preview issues at all.
 */
export function pickPreviewIssueForDate(
  issues: TPreviewIssue[],
  targetDate: Date,
): TPreviewIssue | null {
  if (issues.length === 0) {
    return null;
  }
  const target = dayjs(targetDate).format("YYYYMMDD");
  return (
    issues.find(
      (issue) => dayjs(issue.publishedDate).format("YYYYMMDD") === target,
    ) ?? issues[0]
  );
}
